import React, { Fragment } from "react";
import { Flex, Box } from "grid-styled";
import { graphql, compose } from "react-apollo";
import { gql } from "apollo-boost";
import moment from "moment";
import { withStateHandlers } from "recompose";
import { downloadReport } from "../../../api";
import Button from "../../Button";
import { contactDisplayName } from "../../../utils/helpers";
import { getPreferences, setPreferences } from "../../../utils/localStorage";
import {
  JobName,
  JobDetails,
  ContractInfo,
  FranchiseInfo
} from "../../JobsList";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverContentItem
} from "../../Popover";
import Icon from "../../Icon";
import { MORE } from "../../../icons";
import CenteredLoader from "../../CenteredLoader";
import { Table, TableRow, TableCell } from "../../Table";
import EmptyMessage from "../../EmptyMessage";
import { InputContainer, Select } from "../../forms";
import Modal from "../../Modal";

const columnSizing = "2fr 1fr 1fr 1fr";

const SpanButton = Button.withComponent("span");

class NgcQueue extends React.Component {
  state = {
    fixingBounceForJob: null,
    newEmail: "",
    medium: "",
    downloading: false
  };

  markAsSentViaPost = job => {
    return this.props.markAsSentViaPost(job).then(() => {
      return this.props.data.refetch();
    });
  };

  handleAction = value => {
    value && value();
  };

  handleFixBounceSubmit = e => {
    if (e) e.preventDefault();
    this.fixBounce(this.state.newEmail).then(() => {
      this.closeFixBounceModal();
    });
  };

  handleDropdownAction = value => {
    return value && value();
  };

  handleCSVDownload = evt => {
    evt.preventDefault();
    this.setState({ downloading: true });
    downloadReport("ngc_queue", {
      query: {},
      filename: "NGC Mail Merge"
    }).then(() => {
      this.setState({ downloading: false });
    });
  };

  fixBounce = newEmail => {
    const { fixingBounceForJob: job } = this.state;
    return this.props
      .updateJob({
        id: job.id,
        input: {
          contact: {
            id: job.contact.id,
            email: newEmail
          }
        }
      })
      .then(() => this.props.sendNgcEmail(job))
      .then(() => this.props.data.refetch());
  };

  openFixBounceModal = job => {
    this.setState({
      fixingBounceForJob: job,
      newEmail: job.contact.email || ""
    });
  };

  closeFixBounceModal = () => {
    this.setState({
      fixingBounceForJob: null,
      newEmail: ""
    });
  };

  renderJob = job => {
    const link = `/admin/jobs/${job.id}`;
    const hasBounced =
      job.ngcPackageSent &&
      job.ngcPackageSentVia === "Email" &&
      job.ngcPackageEmailStatus === "bounced";
    return (
      <TableRow key={job.id} columns={columnSizing}>
        <TableCell>
          <span>
            <JobName to={link}>{contactDisplayName(job.contact)}</JobName>
            <JobDetails>
              <ContractInfo job={job} />
              <FranchiseInfo franchise={job.franchise} />
            </JobDetails>
          </span>
        </TableCell>
        <TableCell>{job.ngcPackageSentVia}</TableCell>
        <TableCell>{hasBounced ? "Bounced" : "Unsent"}</TableCell>
        <TableCell justify="flex-end">
          {job.ngcPackageSentVia === "Email" ? (
            <Fragment>
              <Button
                onClick={() => this.openFixBounceModal(job)}
                variant="primary"
              >
                Fix Bounce
              </Button>
              <Popover onSelection={this.handleAction}>
                <PopoverTrigger>
                  <SpanButton>
                    <Icon icon={MORE} />
                  </SpanButton>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverContentItem value={() => this.markAsSentViaPost(job)}>
                    Mark as Sent Via Post
                  </PopoverContentItem>
                </PopoverContent>
              </Popover>
            </Fragment>
          ) : (
            <Button
              variant="primary"
              onClick={() => this.markAsSentViaPost(job)}
            >
              Mark as Sent
            </Button>
          )}
        </TableCell>
      </TableRow>
    );
  };

  renderFixBounceModal() {
    return (
      <Modal
        title="Fixing email address..."
        isOpen={!!this.state.fixingBounceForJob}
        close={this.closeFixBounceModal}
      >
        <Box p={2}>
          <form onSubmit={this.handleFixBounceSubmit}>
            <InputContainer
              name="email"
              labelText="Email Address"
              value={this.state.newEmail}
              onChange={e => this.setState({ newEmail: e.target.value })}
            />
            <Flex pt={2} justify="flex-end">
              <Button
                type="button"
                onClick={this.closeFixBounceModal}
                style={{ marginRight: 4 }}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save and Send
              </Button>
            </Flex>
          </form>
        </Box>
      </Modal>
    );
  }

  render() {
    if (this.props.data) {
      const { loading, jobs } = this.props.data;

      if (loading && !jobs) {
        return <CenteredLoader />;
      }

      return (
        <React.Fragment>
          <Flex pb={2} mx={-2}>
            <Box w={1 / 4} px={2}>
              <InputContainer
                name="medium"
                labelText="Medium"
                placeholder="Filter by medium"
                value={this.props.medium}
                onChange={this.props.setMedium}
                fieldComponent={Select}
                options={["Email", "Post"].map(value => ({
                  value,
                  label: value
                }))}
              />
            </Box>
            <Flex ml="auto" align="flex-end" justify="flex-end" px={2}>
              <Button onClick={this.handleCSVDownload}>
                {this.state.downloading
                  ? "Downloading CSV..."
                  : "Download Mail Merge CSV"}
              </Button>
            </Flex>
          </Flex>
          {!jobs || !jobs.length ? (
            <EmptyMessage>It looks like the NGC queue is empty!</EmptyMessage>
          ) : (
            <Table>
              <TableRow header columns={columnSizing}>
                <TableCell />
                <TableCell>Via</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
              {jobs.map(this.renderJob)}
            </Table>
          )}
          {this.renderFixBounceModal()}
        </React.Fragment>
      );
    }

    return null;
  }
}

const fragments = {
  job: gql`
    fragment NgcQueueJob on Job {
      id
      contractNumber
      ngcPackageSent
      ngcPackageSentOn
      ngcPackageSentVia
      ngcPackageEmailedTo
      ngcPackageEmailStatus
      ngcPackageEmailOpened
      contact {
        id
        firstName
        lastName
        otherFirstName
        otherLastName
        email
      }
      franchise {
        id
        name
        assignedInHouseTty
      }
    }
  `
};

const NgcQueueQuery = gql`
  query NgcQueueQuery($medium: String) {
    jobs(queue: "ngc", ngcSentVia: $medium) {
      ...NgcQueueJob
    }
  }
  ${fragments.job}
`;

const SendNgcEmailMutation = gql`
  mutation SendNgcEmailMutation($id: ID!) {
    sendNgcEmail(id: $id) {
      ...NgcQueueJob
    }
  }
  ${fragments.job}
`;

const UpdateJobMutation = gql`
  mutation UpdateJobMutation($id: ID!, $input: JobInput!) {
    updateJob(id: $id, input: $input) {
      ...NgcQueueJob
    }
  }
  ${fragments.job}
`;

const preferenceKey = "ngcQueueFilters";

const setAndSaveState = key => value => {
  const updatedState = { [key]: value };
  setPreferences(preferenceKey, updatedState);
  return updatedState;
};

const enhance = compose(
  withStateHandlers(
    () => ({
      medium: "",
      ...getPreferences(preferenceKey)
    }),
    {
      setMedium: () => setAndSaveState("medium")
    }
  ),
  graphql(SendNgcEmailMutation, {
    props: ({ mutate }) => ({
      sendNgcEmail({ id }) {
        return mutate({
          variables: { id }
        });
      }
    })
  }),
  graphql(UpdateJobMutation, {
    props: ({ mutate }) => ({
      updateJob({ id, input }) {
        return mutate({
          variables: { id, input }
        });
      },
      markAsSentViaPost({ id }) {
        return mutate({
          variables: {
            id,
            input: {
              ngcPackageSent: true,
              ngcPackageSentOn: moment().format("YYYY-MM-DD"),
              ngcPackageSentVia: "Post"
            }
          }
        });
      }
    })
  }),
  graphql(NgcQueueQuery, {
    options: ({ medium }) => ({
      pollInterval: 1000 * 30,
      variables: { medium },
      fetchPolicy: "network-only"
    })
  })
);

export default enhance(NgcQueue);



// WEBPACK FOOTER //
// ./src/components/admin/Dashboard/NgcQueue.js